<template>
  <div class="quill-editor-wrapper">
    <quill-editor
      ref="quillEditor"
      v-model="content"
      :options="editorOption"
      :style="{ height: height }"
      @blur="onBlur"
      @focus="onFocus"
      @input="onInput"
    ></quill-editor>
    <div class="word-count">
      字数: {{ wordCount }}
    </div>
  </div>
</template>

<script>
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.snow.css'

export default {
  name: 'QuillEditor',
  components: {
    quillEditor
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: '500px'
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      content: this.value,
      wordCount: 0,
      editorOption: {
        theme: 'snow',
        readOnly: this.readonly,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],        // 加粗、斜体、下划线、删除线
            ['blockquote', 'code-block'],                      // 引用、代码块
            [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }], // 标题1-3
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],     // 有序列表、无序列表
            [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }], // 对齐方式
            ['clean'],                                         // 清除格式
            ['link']                                           // 链接
          ]
        }
      }
    }
  },
  watch: {
    value(newVal) {
      this.content = newVal
    },
    readonly(newVal) {
      this.editorOption.readOnly = newVal
    }
  },
  mounted() {
    this.wordCount = this.getWordCount(this.content)
    this.$emit('on-word-count-change', this.wordCount)
  },
  methods: {
    onInput() {
      this.$emit('input', this.content)
      this.wordCount = this.getWordCount(this.content)
      this.$emit('on-word-count-change', this.wordCount)
    },
    onBlur() {
      this.$emit('blur')
    },
    onFocus() {
      this.$emit('focus')
    },
    getWordCount(html) {
      if (!html) return 0
      const text = html.replace(/<[^>]*>/g, '')
      const chineseChars = text.match(/[\u4e00-\u9fa5]/g) || []
      const englishWords = text.match(/[a-zA-Z]+/g) || []
      const numbers = text.match(/\d+/g) || []
      return chineseChars.length + englishWords.length + numbers.length
    },
    // 获取编辑器实例
    getEditorInstance() {
      return this.$refs.quillEditor.quill
    },
    // 清空内容
    clear() {
      this.content = ''
    },
    // 获取纯文本
    getPlainText() {
      const editor = this.getEditorInstance()
      return editor ? editor.getText() : ''
    },
    // 获取HTML内容
    getHTML() {
      return this.content
    }
  }
}
</script>

<style lang="scss" scoped>
// ─── Token System (shared with AiGenArticleList) ───
$border: #E8E6E3;
$border-light: #F0EEEC;
$text: #1A1A1A;
$text-secondary: #57534E;
$text-muted: #A8A29E;
$teal: #0F766E;
$surface: #FFFFFF;
$paper-bg: #FDFCFB;

.quill-editor-wrapper {
  display: flex;
  flex-direction: column;
  border: 1px solid $border;
  border-radius: 6px;
  overflow: hidden;
  background: $surface;

  // ─── Editor Content Area (the "page") ───
  :deep(.ql-container) {
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB',
      'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 15px;
    line-height: 1.85;
    color: $text;
    border: none;
  }

  :deep(.ql-editor) {
    min-height: 300px;
    padding: 28px 36px;
    background: $paper-bg;

    // ─── Heading Styles ───
    h1 {
      font-size: 22px;
      font-weight: 700;
      margin: 24px 0 14px;
      color: $text;
      letter-spacing: -0.3px;
    }

    h2 {
      font-size: 19px;
      font-weight: 700;
      margin: 22px 0 12px;
      color: $text;
      letter-spacing: -0.2px;
      text-indent: 0;
    }

    h3 {
      font-size: 16px;
      font-weight: 600;
      margin: 18px 0 8px;
      color: $text-secondary;
      text-indent: 0;
    }

    // ─── Paragraph: manuscript-style ───
    p {
      margin: 6px 0;
      text-align: justify;
      text-indent: 2em;
      color: $text;
    }

    // ─── Lists ───
    ul, ol {
      padding-left: 24px;
      margin: 10px 0;
    }

    li {
      margin: 4px 0;
      line-height: 1.85;
    }

    // ─── Emphasis ───
    strong {
      font-weight: 700;
      color: $text;
    }

    em {
      font-style: italic;
      color: $text-secondary;
    }

    u {
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    // ─── Blockquote: clinical reference style ───
    blockquote {
      border-left: 3px solid $teal;
      margin: 16px 0;
      padding: 10px 16px;
      color: $text-secondary;
      background: #F7F7F5;
      border-radius: 0 4px 4px 0;
      font-size: 14px;

      p {
        text-indent: 0;
        margin: 4px 0;
      }
    }

    code {
      background: #F4F4F2;
      padding: 2px 6px;
      border-radius: 3px;
      font-family: 'SF Mono', 'Fira Code', 'Monaco', monospace;
      font-size: 13px;
      color: #B91C1C;
    }

    // ─── Alignment ───
    .ql-align-center {
      text-align: center;
      text-indent: 0;
    }

    .ql-align-right {
      text-align: right;
      text-indent: 0;
    }
  }

  // ─── Toolbar ───
  :deep(.ql-toolbar) {
    background: #FAFAF9;
    border-bottom: 1px solid $border;
    padding: 6px 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
  }

  :deep(.ql-toolbar .ql-formats) {
    margin-right: 8px;

    &::after {
      content: '';
      display: inline-block;
      width: 1px;
      height: 20px;
      background: $border-light;
      margin-left: 8px;
      vertical-align: middle;
    }

    &:last-child::after {
      display: none;
    }
  }

  :deep(.ql-toolbar button) {
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: $text-secondary;
    border-radius: 4px;
    transition: all 0.15s;
    padding: 0;

    &:hover {
      color: $teal;
      background: #F0FDFA;
    }

    &.ql-active {
      color: $teal;
      background: #F0FDFA;
    }
  }

  :deep(.ql-toolbar .ql-stroke) {
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
  }

  :deep(.ql-toolbar .ql-fill) {
    fill: currentColor;
    stroke: none;
  }

  :deep(.ql-toolbar .ql-picker-label) {
    color: $text-secondary;
    border-radius: 4px;
    padding: 2px 8px;
    font-size: 13px;

    &:hover {
      color: $teal;
    }
  }

  :deep(.ql-toolbar .ql-picker-options) {
    border-color: $border;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 4px;
  }

  :deep(.ql-toolbar .ql-picker-item) {
    border-radius: 4px;
    padding: 4px 8px;

    &:hover {
      background: #F0FDFA;
      color: $teal;
    }
  }
}

// ─── Word Count Bar ───
.word-count {
  padding: 6px 16px;
  background: #FAFAF9;
  border-top: 1px solid $border;
  font-size: 11px;
  color: $text-muted;
  text-align: right;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}
</style>